import React, { useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useActivityCarousel } from 'hooks/useActivityCarousel';
import { ActivityCard } from './ActivityCard';
import { CarouselNavigation } from './CarouselNavigation';
import { ActivityCardSkeleton } from './ActivityCardSkeleton';
import { trackEvent } from 'utils/analytics';
import './RecentActivityCarousel.css';

interface RecentActivityCarouselProps {
  /** Enable WebSocket for real-time updates */
  enableSocket?: boolean;
}

/**
 * RecentActivityCarousel - Displays recent platform activity in a carousel
 * Shows trades sent/received and want list additions
 */
export const RecentActivityCarousel: React.FC<RecentActivityCarouselProps> = ({
  enableSocket = true,
}) => {
  const {
    visibleActivities,
    isLoading,
    error,
    currentPage,
    totalPages,
    itemsPerPage,
    hasPrevPage,
    hasNextPage,
    goNext,
    goPrevious,
    refetch,
  } = useActivityCarousel({ enableSocket });

  const carouselViewedRef = useRef(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<'exit' | 'enter' | null>(null);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);

  // Track carousel view on mount
  useEffect(() => {
    if (!carouselViewedRef.current && !isLoading && visibleActivities.length > 0) {
      carouselViewedRef.current = true;
      trackEvent('carousel_viewed', {
        activity_count: visibleActivities.length,
        total_pages: totalPages,
      });
    }
  }, [isLoading, visibleActivities.length, totalPages]);

  // Handle navigation with animation
  const handleNavigate = (direction: 'left' | 'right') => {
    const isNext = direction === 'right';
    if ((isNext && !hasNextPage) || (!isNext && !hasPrevPage)) return;
    if (isAnimating) return;

    trackEvent('carousel_arrow_clicked', {
      direction,
      current_page: currentPage,
      total_pages: totalPages,
    });

    // Phase 1: Exit animation
    setSlideDirection(direction);
    setAnimationPhase('exit');
    setIsAnimating(true);

    // Phase 2: Change page and enter animation
    setTimeout(() => {
      if (isNext) {
        goNext();
      } else {
        goPrevious();
      }
      setAnimationPhase('enter');

      // Phase 3: Reset after enter animation completes
      setTimeout(() => {
        setIsAnimating(false);
        setAnimationPhase(null);
        setSlideDirection(null);
      }, 150);
    }, 150);
  };

  // Swipe handlers for mobile
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleNavigate('right'),
    onSwipedRight: () => handleNavigate('left'),
    trackMouse: false,
    delta: 50,
    preventScrollOnSwipe: true,
  });

  // Error state
  if (error) {
    return (
      <div className="recent-activity-carousel">
        <div className="recent-activity-carousel__header">
          <h3 className="recent-activity-carousel__title">Recent Activity</h3>
        </div>
        <div className="recent-activity-carousel__error">
          <p>Unable to load recent activity</p>
          <button
            className="recent-activity-carousel__retry-button"
            onClick={() => refetch()}
            type="button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="recent-activity-carousel">
        <div className="recent-activity-carousel__header">
          <h3 className="recent-activity-carousel__title">Recent Activity</h3>
        </div>
        <div className="recent-activity-carousel__content">
          <div className="recent-activity-carousel__cards">
            <ActivityCardSkeleton count={itemsPerPage} />
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (visibleActivities.length === 0) {
    return (
      <div className="recent-activity-carousel">
        <div className="recent-activity-carousel__header">
          <h3 className="recent-activity-carousel__title">Recent Activity</h3>
        </div>
        <div className="recent-activity-carousel__empty">
          <p>No recent activity yet. Be the first to start trading!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="recent-activity-carousel">
      <div className="recent-activity-carousel__header">
        <h3 className="recent-activity-carousel__title">Recent Activity</h3>
      </div>

      <div className="recent-activity-carousel__content" {...swipeHandlers}>
        {/* Left navigation */}
        <CarouselNavigation
          direction="left"
          onClick={() => handleNavigate('left')}
          disabled={!hasPrevPage || isAnimating}
        />

        {/* Cards container */}
        <div
          className={`recent-activity-carousel__cards${
            animationPhase && slideDirection
              ? ` recent-activity-carousel__cards--${animationPhase}-${
                  animationPhase === 'exit'
                    ? (slideDirection === 'right' ? 'left' : 'right')
                    : slideDirection
                }`
              : ''
          }`}
        >
          {visibleActivities.map((activity, index) => (
            <ActivityCard
              key={activity.id}
              activity={activity}
              position={(currentPage - 1) * itemsPerPage + index}
            />
          ))}
        </div>

        {/* Right navigation */}
        <CarouselNavigation
          direction="right"
          onClick={() => handleNavigate('right')}
          disabled={!hasNextPage || isAnimating}
        />
      </div>

      {/* Page indicator (optional) */}
      {totalPages > 1 && (
        <div className="recent-activity-carousel__pagination">
          {Array.from({ length: totalPages }).map((_, index) => (
            <span
              key={index}
              className={`recent-activity-carousel__dot ${
                currentPage === index + 1
                  ? 'recent-activity-carousel__dot--active'
                  : ''
              }`}
              aria-label={`Page ${index + 1} of ${totalPages}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentActivityCarousel;
